import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { PROGRAMS } from "@/components/site/Programs";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import ProgramHero from "./_partials/ProgramHero";
import ProgramProtocol from "./_partials/ProgramProtocol";
import ProgramDepth from "./_partials/ProgramDepth";
import ProgramAdjacentNav from "./_partials/ProgramAdjacentNav";
import BodyTransformationMobileTech from "./_partials/BodyTransformationMobileTech";
import { PROGRAM_DEPTH } from "@/data/programDepth";
import {
  organizationSchema,
  medicalWebPageSchema,
  wellnessWebPageSchema,
  breadcrumbSchema,
  faqSchema,
  programOgImage, SITE_URL} from "@/lib/seo";

const RHEA_AVATAR =
  "https://cdn.jotfor.ms/assets/agent-avatars/avatar_icon_79.png";
const VIEWPORT_ONCE = { once: true };
const FADE_UP_VIEW = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT_ONCE,
  transition: { duration: 0.6 },
};

// Programs delivered as licensed medical care (under MD oversight).
const MEDICAL_SLUGS = new Set([
  "hormone-optimization",
  "longevity-optimization",
  "metabolic-optimization",
  "health-optimization",
]);

const PROGRAM_META = {
  "hormone-optimization": {
    title:
      "Hormone Optimization Program | AI Digital Twin Protocols | Integrated Clinical Intelligence",
    description:
      "Precision hormone optimization powered by AI digital twin analysis. Lab-driven protocols for testosterone, thyroid, and metabolic balance. 50-state telemedicine.",
  },
  "longevity-optimization": {
    title:
      "Longevity Optimization Program | Biological Age & Healthspan | Integrated Wellness",
    description:
      "Evidence-informed longevity protocols built from your DNA, microbiome, biomarkers, and wearables. Physician-reviewed precision programs designed to support healthy biological aging.",
  },
  "metabolic-optimization": {
    title:
      "Metabolic Optimization Program | CGM, Mitochondria & Longevity | Integrated Clinical Intelligence",
    description:
      "Metabolic optimization protocols built from CGM, insulin, mitochondrial markers, and oxidative stress data. Tune nutrition and movement at the cellular level.",
  },
  "health-optimization": {
    title:
      "Health Optimization Program | AI Digital Twin Baseline | Integrated Clinical Intelligence",
    description:
      "Whole-person health baseline — labs, biomarkers, wearables, sleep, recovery — synthesized into your Digital Twin and a precision intervention sequence.",
  },
  "performance-optimization": {
    title:
      "Performance Optimization Program | AI Wellness for Executives & Athletes | Integrated Clinical Intelligence",
    description:
      "Performance wellness for executives, athletes, and high-output professionals. AI-built recovery, training, and optimization protocols from your biometric data.",
  },
  "body-transformation": {
    title:
      "Body Transformation Program | Muscle, Fat & Mobility | Integrated Clinical Intelligence",
    description:
      "Body transformation built on data — patent-pending body composition tracking, hormone & recovery data, sustainable strength and mobility programming. No crash diets.",
  },
  "cognitive-optimization": {
    title:
      "Cognitive Optimization Program | Sharper Focus, Deeper Recall | Integrated Clinical Intelligence",
    description:
      "Cognitive optimization powered by AI digital twin analysis — sleep and circadian engineering, targeted nootropic stack, and nervous-system rehab for lasting focus.",
  },
};

export default function ProgramPage() {
  const { slug } = useParams();
  const program = PROGRAMS.find((p) => p.slug === slug);
  const isMedical = MEDICAL_SLUGS.has(slug || "");
  const customMeta = PROGRAM_META[slug || ""];

  // Per-page meta + OG (must run unconditionally for rules-of-hooks)
  useDocumentMeta({
    title:
      customMeta?.title ||
      (program
        ? `${program.title} — Integrated Clinical Intelligence`
        : "Integrated Clinical Intelligence"),
    description: customMeta?.description || (program ? program.pitch : ""),
    image: program ? programOgImage(program.slug) : null,
    url: program ? `${SITE_URL}/programs/${program.slug}` : undefined,
    schema: program
      ? [
          organizationSchema(),
          isMedical
            ? medicalWebPageSchema({
                name: `${program.title} | Integrated Wellness`,
                about: program.title,
              })
            : wellnessWebPageSchema({
                name: `${program.title} | Integrated Wellness`,
              }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Programs", url: "/#programs" },
            { name: program.title, url: `/programs/${program.slug}` },
          ]),
          ...(PROGRAM_DEPTH[slug]?.faqs ? [faqSchema(PROGRAM_DEPTH[slug].faqs)] : []),
        ]
      : undefined,
  });

  // Adjacent programs for the bottom nav (computed safely if program is missing)
  const idx = program ? PROGRAMS.findIndex((p) => p.slug === slug) : 0;
  const prev = PROGRAMS[(idx - 1 + PROGRAMS.length) % PROGRAMS.length];
  const next = PROGRAMS[(idx + 1) % PROGRAMS.length];

  if (!program) return <Navigate to="/" replace />;

  return (
    <div data-testid={`program-page-${slug}`} className="min-h-screen bg-background">
      <Header />
      <main className="pt-32 md:pt-36 pb-16 md:pb-24">
        {/* Breadcrumb */}
        <div className="max-w-6xl mx-auto px-4 md:px-8 mb-8">
          <Link
            to="/#programs"
            data-testid="program-back-link"
            className="eyebrow inline-flex items-center gap-2 text-foreground/65 hover:text-primary transition-colors"
          >
            <ArrowLeft size={12} weight="bold" /> Back to all programs
          </Link>
        </div>

        <ProgramHero program={program} />

        <ProgramProtocol program={program} />

        {slug === "body-transformation" && <BodyTransformationMobileTech />}

        <ProgramDepth program={program} depth={PROGRAM_DEPTH[slug]} isMedical={isMedical} />

        {/* Empower Doctors callout */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 mt-20 md:mt-28">
          <motion.div
            {...FADE_UP_VIEW}
            className="text-center p-10 md:p-14 bg-foreground text-background rounded-3xl border-2 border-foreground/40 shadow-xl"
          >
            <div className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent mb-4">
              Our principle
            </div>
            <p
              className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight tracking-tight"
              style={{ color: "hsl(38 38% 95%)" }}
            >
              We are not here to <em className="not-italic">replace</em> doctors —
              <br />
              <span className="text-accent">we empower them</span>.
            </p>
            <p className="mt-5 text-base md:text-lg text-background/75 max-w-2xl mx-auto leading-relaxed">
              Every protocol is delivered with — and titrated alongside — your physician.
            </p>
          </motion.div>
        </section>

        {/* Talk to Rhea CTA */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 mt-20 md:mt-28">
          <motion.div
            {...FADE_UP_VIEW}
          >
            <Link
              to="/agents/rhea"
              data-testid="program-rhea-cta"
              className="group flex flex-col sm:flex-row items-center gap-6 p-7 md:p-9 bg-card border-2 border-primary/40 rounded-3xl shadow-lg hover:border-primary hover:shadow-2xl hover:-translate-y-0.5 transition-all"
            >
              <img
                src={RHEA_AVATAR}
                alt="Rhea — Integrated Optimization Agent"
                className="h-20 w-20 md:h-24 md:w-24 rounded-2xl object-cover bg-primary/10 flex-shrink-0 ring-2 ring-primary/30"
              />
              <div className="flex-1 text-center sm:text-left">
                <div className="eyebrow text-[0.6rem] mb-2">Live · AI Agent</div>
                <div className="font-display text-2xl md:text-3xl font-semibold leading-tight">
                  Talk to Rhea about{" "}
                  <span className="text-primary">{program.title}</span>
                </div>
                <p className="mt-2 text-base text-foreground/70">
                  Tell Rhea your goals — she&rsquo;ll guide you to the right next step.
                </p>
              </div>
              <span className="iw-btn-red inline-flex items-center gap-2 px-6 py-3 text-[0.7rem] group-hover:gap-3 transition-all flex-shrink-0">
                Start <ArrowRight size={16} weight="bold" />
              </span>
            </Link>
          </motion.div>
        </section>

        <ProgramAdjacentNav prev={prev} next={next} />
      </main>
      <Footer />
    </div>
  );
}
